import type { IconName } from "../components/AppIcon";

/**
 * Module catalog rendered in the brochure overlay (Phase 4) and — when shipped —
 * the per-module pages under /brochure/<slug> (Phase 6 owns those).
 *
 * Keep this list ordered by the user journey, not alphabetically. Attendance and
 * Leaves are the first things most HR teams want to see; Tournament/Projects sit
 * at the end because they're discovery surfaces.
 *
 * Icons must exist in lucide-react (verify via AppIcon's IconName union).
 * Accent classes follow the muted-tint convention used elsewhere in the app:
 * a soft bg-* paired with a slightly stronger text-* / ring-* so the cards
 * read as a coherent set rather than a clown-car of saturated tiles.
 */

export type ModuleSlug =
  | "attendance"
  | "leaves"
  | "recruitment"
  | "impact"
  | "development"
  | "payroll"
  | "tournament"
  | "projects";

export interface BrochureModule {
  slug: ModuleSlug;
  /** i18n key — translated text appears in en/common.json + ar/common.json. */
  nameKey: string;
  /** One-sentence pitch (i18n key). Reads in flowing English/Arabic, not bullet copy. */
  pitchKey: string;
  /** lucide-react icon name — must be a real `IconName`. */
  iconName: IconName;
  /**
   * Tailwind accent tokens. We split bg / text / ring so the card surface,
   * the icon foreground, and the focus ring can each carry the tone without
   * tripping over each other.
   */
  accent: {
    surface: string; // soft tinted background for the icon chip
    iconText: string; // icon foreground colour
    ring: string; // hover/focus ring colour
  };
}

export const BROCHURE_MODULES: BrochureModule[] = [
  {
    slug: "attendance",
    nameKey: "brochure.module.attendance.name",
    pitchKey: "brochure.module.attendance.pitch",
    iconName: "Clock",
    accent: {
      surface: "bg-indigo-50",
      iconText: "text-indigo-600",
      ring: "hover:ring-indigo-300/60",
    },
  },
  {
    slug: "leaves",
    nameKey: "brochure.module.leaves.name",
    pitchKey: "brochure.module.leaves.pitch",
    iconName: "Calendar",
    accent: {
      surface: "bg-amber-50",
      iconText: "text-amber-700",
      ring: "hover:ring-amber-300/60",
    },
  },
  {
    slug: "recruitment",
    nameKey: "brochure.module.recruitment.name",
    pitchKey: "brochure.module.recruitment.pitch",
    iconName: "UserPlus",
    accent: {
      surface: "bg-sky-50",
      iconText: "text-sky-600",
      ring: "hover:ring-sky-300/60",
    },
  },
  {
    slug: "impact",
    nameKey: "brochure.module.impact.name",
    pitchKey: "brochure.module.impact.pitch",
    iconName: "Sparkles",
    accent: {
      surface: "bg-rose-50",
      iconText: "text-rose-600",
      ring: "hover:ring-rose-300/60",
    },
  },
  {
    slug: "development",
    nameKey: "brochure.module.development.name",
    pitchKey: "brochure.module.development.pitch",
    iconName: "TrendingUp",
    accent: {
      surface: "bg-emerald-50",
      iconText: "text-emerald-700",
      ring: "hover:ring-emerald-300/60",
    },
  },
  {
    slug: "payroll",
    nameKey: "brochure.module.payroll.name",
    pitchKey: "brochure.module.payroll.pitch",
    iconName: "Wallet",
    accent: {
      surface: "bg-violet-50",
      iconText: "text-violet-700",
      ring: "hover:ring-violet-300/60",
    },
  },
  {
    slug: "tournament",
    nameKey: "brochure.module.tournament.name",
    pitchKey: "brochure.module.tournament.pitch",
    iconName: "Trophy",
    accent: {
      surface: "bg-orange-50",
      iconText: "text-orange-600",
      ring: "hover:ring-orange-300/60",
    },
  },
  {
    slug: "projects",
    nameKey: "brochure.module.projects.name",
    pitchKey: "brochure.module.projects.pitch",
    iconName: "Layers",
    accent: {
      surface: "bg-teal-50",
      iconText: "text-teal-700",
      ring: "hover:ring-teal-300/60",
    },
  },
];

/**
 * 🔴 Modules NEVER showcased to a Mowazaf Connect (tier `Free`) tenant. Owner request 2026-10-05:
 * "On Mowazaf Connect, please remove showcasing the recruitment module and the tournaments module."
 *
 * THE ONE RULE. Every Connect upsell surface reads this set instead of carrying its own list:
 *   - `useFilteredNavigationState` drops these modules from the sidebar / outlet navigation;
 *   - `MasterLayout` turns their routes (LockedShell candidates) and `/brochure/<slug>` into the
 *     same "turned off" notice + redirect that `PageConfigGuard` uses for a hidden page;
 *   - `FreeTierShowcase` (dashboard grid) and `BrochureOverlay` (banner grid) leave them out;
 *   - server-side, `NavigationCatalogResolver` mirrors it for the AI assistant's wayfinding.
 *
 * Scope is tier-derived and LIVE: nothing is written to module/page config, so Paid, Trial and
 * HRMS tenants (prod co 29 runs tournaments), Operators, and a tenant that upgrades all keep both
 * modules exactly as before. Do NOT delete the two `BROCHURE_MODULES` entries or their brochure
 * components: `BrochureRouter`'s exhaustive switch and the paid-expiring / trial overlay need them.
 *
 * Module ids in `navigationModules.ts` equal these slugs (`recruitment`, `tournament`), so the same
 * set serves both the navigation filter and the brochure catalog.
 */
export const CONNECT_HIDDEN_MODULE_SLUGS: ReadonlySet<ModuleSlug> = new Set<ModuleSlug>([
  "recruitment",
  "tournament",
]);

/** True when `id` (a nav module id or a brochure slug) must never be showcased on Connect. */
export function isConnectHiddenModule(id: string | null | undefined): boolean {
  return !!id && CONNECT_HIDDEN_MODULE_SLUGS.has(id as ModuleSlug);
}

/** The brochure catalog as Connect (Free) is allowed to see it. */
export const CONNECT_SHOWCASE_MODULES: readonly BrochureModule[] = BROCHURE_MODULES.filter(
  (m) => !CONNECT_HIDDEN_MODULE_SLUGS.has(m.slug),
);

/** The brochure catalog for a tier: the Connect subset for `Free`, the full list for everyone else. */
export function showcaseModulesFor(tier: string | null | undefined): readonly BrochureModule[] {
  return tier === "Free" ? CONNECT_SHOWCASE_MODULES : BROCHURE_MODULES;
}

/**
 * The brochure slug a pathname targets (`/brochure/<slug>`), or null. Lets `MasterLayout` refuse a
 * Connect-hidden brochure by URL before `BrochureRouter` renders it.
 */
export function getBrochureSlugFromPath(pathname: string): string | null {
  const m = /^\/brochure\/([^/?#]+)\/?$/i.exec(pathname || "");
  return m ? m[1].toLowerCase() : null;
}
